import { Point2D } from '@shared/models/geometry.model';
import {
  Shape,
  Storey,
  STOREY_MIN_HEIGHT,
} from '@shared/models/design/storey.model';
import { Activity } from '../../../models/design/activities/activity.model';
import {
  GenericValidationError,
  StoreyValidationError,
  ValidatedValue,
  ValidationResult,
} from '../../../validation/validation.models';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  computePolygonArea,
  getPointOnLine,
  isEqualPoints,
  isPolygon2D,
} from '@shared/helpers/geometry/geometry.helpers';
import { isPolygonSelfIntersecting } from '@shared/helpers/geometry/polygon.helpers';
import { shapePointsExceedMaxSeparation } from '@shared/helpers/geometry/point-separation.helpers';
import { designGuards } from '@shared/helpers/design/design-typeguards.helpers';
import { isValidActivityReferences } from './activity-references.validation';
import { ActivityReferencesValidationErrors } from './activity-references.validation';
import {
  doBuildingSolidsIntersect,
  doShapesIntersect,
  isPointInsideStoreyShapes,
  isPolygonInsideAnyStoreyCutout,
  isSolidPairAllowedByCutoutHole,
} from '../shape.helpers';
import { findDuplicates } from '@shared/helpers/array.helpers';
import {
  Building,
  BuildingDesign,
} from '@shared/models/design/building-design.model';
import { isCloseTo } from '@shared/helpers/math.helpers';
import { getStoreyBelow } from '../storey.helpers';
import { isMezzanineAllowed } from '../mezzanine-helpers';
import {
  CUTOUT_MIN_MARGIN_METERS,
  getCutoutShapes,
  getSolidShapes,
  isCutoutInsideStoreyWithMargin,
} from '../cutout.helpers';

export const StoreyValidationErrors = {
  STOREY_HEIGHT_POSITIVE: 'Storey height must be at least 1',
  STOREY_SHAPES_ARRAY: 'Storey shapes must be an array',
  STOREY_POLYGON_ARRAY: 'Storey polygon must be an array if provided',
  SHAPE_POINTS_ARRAY:
    'Shape points must be an array with at least 3 points [x, y] pairs',
  SHAPE_DRAFT_POINTS_ARRAY: 'Draft shape is not allowed to save',
  POINT_COORDS_NUMBER: 'Point coordinates must be finite numbers',
  POINT_SEPARATION_TOO_LARGE:
    'A point must not be more than 5000 m from another point',
  SHAPE_AREA_TOO_LARGE: 'Gross floor area must not exceed 100 000 m²',
  SHAPE_SELF_INTERSECTING: 'Shape edges must not intersect each other',
  SHAPES_OVERLAP: 'Storey shapes must not overlap',
  BUILDINGS_OVERLAP: 'Buildings must not overlap',
  DUPLICATE_STOREY_IDS: 'Duplicate storey ids',
  STOREYS_NOT_ARRAY: 'Storeys must be an array of at least 1 storey',
  BUILDINGS_EMPTY: 'Design must have at least one building',
  DUPLICATE_BUILDING_IDS: 'Duplicate building ids',
  BOTTOM_STOREY_MULTIPLE_SHAPES: "Bottom storey can't have multiple shapes",
  DUPLICATE_SHAPE_IDS: 'Duplicate shape ids',
  SHAPES_NOT_CONNECTED: 'Storey not connected to storey below',
  MEZZANINE_BOTTOM_FORBIDDEN: 'Ground floor cannot be a mezzanine',
  MEZZANINE_PARENT_MISSING: 'Mezzanine storey is missing a parent storey',
  MEZZANINE_ELEVATION_RANGE:
    'Mezzanine elevation must be greater than 0 and below parent height',
  MEZZANINE_HEIGHT_MISMATCH:
    'Mezzanine height must equal parent height minus elevation',
  MEZZANINE_POINTS_OUTSIDE:
    'All mezzanine shapes must be inside the parent storey',
  MEZZANINE_CUTOUT_FORBIDDEN: 'Mezzanine storeys cannot contain cutouts',
  MEZZANINE_NOT_ALLOWED:
    'Mezzanine is not allowed for this storey (ground floor, short parent, or cutouts on this or the parent storey)',
  CUTOUT_PARENT_MISSING:
    'Cutout shape must be inside a non-cutout shape on the same storey',
  CUTOUT_MARGIN_TOO_SMALL:
    'Cutout shape must keep at least 1 meter distance to enclosing shape edge',
  DUPLICATE_POINTS: 'Duplicate points in shape',
  ...ActivityReferencesValidationErrors,
} as const;

/**
 * Check if a shape is valid
 * @param shape Shape to validate
 * @returns ValidationResult
 */
const isValidShape = (shape: Shape): ValidationResult => {
  if (designGuards.is.draftShape(shape)) {
    return new GenericValidationError(
      StoreyValidationErrors.SHAPE_DRAFT_POINTS_ARRAY,
    );
  }
  const { points } = shape;

  if (shapePointsExceedMaxSeparation(points)) {
    return new GenericValidationError(
      StoreyValidationErrors.POINT_SEPARATION_TOO_LARGE,
    );
  }

  // Validate that a shape is not larger than 100k square meters
  const area = computePolygonArea(points);
  if (area > 100_000) {
    return new GenericValidationError(
      StoreyValidationErrors.SHAPE_AREA_TOO_LARGE,
    );
  }

  // Points must be a valid polygon (3 or more points)
  if (!isPolygon2D(points)) {
    return new GenericValidationError(
      StoreyValidationErrors.SHAPE_POINTS_ARRAY,
    );
  }

  // Check for self-intersecting edges
  if (isPolygonSelfIntersecting(points)) {
    return new GenericValidationError(
      StoreyValidationErrors.SHAPE_SELF_INTERSECTING,
    );
  }

  return true;
};

/**
 * Check if a storey is valid
 * @param storey Storey to validate
 * @param activities If activities are provided, validate the activity references else ignore them
 * @returns ValidationResult
 */
export const isValidStorey = (
  storey: Storey,
  activities?: Activity[],
): ValidationResult => {
  const { height, shapes } = storey;

  if (!guards.is.finiteNumber(height) || height < STOREY_MIN_HEIGHT) {
    return new StoreyValidationError(
      StoreyValidationErrors.STOREY_HEIGHT_POSITIVE,
      { storey },
    );
  }

  // Shapes must be an array
  if (!guards.is.array(shapes)) {
    return new StoreyValidationError(
      StoreyValidationErrors.STOREY_SHAPES_ARRAY,
      { storey },
    );
  }
  // Check each shape is valid
  for (const shape of shapes) {
    const validShape = isValidShape(shape);
    if (validShape !== true) return validShape;
  }

  // Check for duplicate shape ids
  const shapeIdDuplicates = findDuplicates(shapes.map((s) => s.id));
  if (shapeIdDuplicates.length) {
    return new StoreyValidationError(
      StoreyValidationErrors.DUPLICATE_SHAPE_IDS,
      { storey },
    );
  }

  // Make sure we don't have 2 points on the same position
  const pointDuplicates = getPointDuplicates(
    storey.shapes.flatMap((s) => s.points),
  );

  if (pointDuplicates.length) {
    console.error('Duplicate points in shape', pointDuplicates, storey);
    return new GenericValidationError(StoreyValidationErrors.DUPLICATE_POINTS);
  }

  if (
    designGuards.is.mezzanineStorey(storey) &&
    getCutoutShapes(storey).length
  ) {
    return new StoreyValidationError(
      StoreyValidationErrors.MEZZANINE_CUTOUT_FORBIDDEN,
      { storey },
    );
  }

  // Check for overlapping shapes
  for (let i = 0; i < shapes.length; i++) {
    const shapeA = shapes[i];
    if (!shapeA) continue;
    for (let j = i + 1; j < shapes.length; j++) {
      const shapeB = shapes[j];
      if (!shapeB) continue;
      const isCutoutPair = shapeA.cutout === true || shapeB.cutout === true;
      // Cutout-over-solid overlap is expected by design; validated separately.
      if (isCutoutPair && !(shapeA.cutout === true && shapeB.cutout === true)) {
        continue;
      }
      if (doShapesIntersect(shapeA, shapeB)) {
        // Courtyard house: solid fully in a cutout may sit inside the outer ring.
        if (isSolidPairAllowedByCutoutHole(shapeA, shapeB, storey)) continue;
        return new StoreyValidationError(
          StoreyValidationErrors.SHAPES_OVERLAP,
          { storey },
        );
      }
    }
  }

  const validCutouts = validateStoreyCutouts(storey);
  if (validCutouts !== true) return validCutouts;

  // Activity references
  const validActivityRefs = isValidActivityReferences(storey, activities);
  if (validActivityRefs !== true) return validActivityRefs;

  return true;
};

/**
 * Validate cutout topology and required containment constraints.
 * @param storey
 */
const validateStoreyCutouts = (storey: Storey): ValidationResult => {
  const cutouts = getCutoutShapes(storey);
  if (!cutouts.length) return true;
  if (!getSolidShapes(storey).length) {
    return new StoreyValidationError(
      StoreyValidationErrors.CUTOUT_PARENT_MISSING,
      {
        storey,
      },
    );
  }

  for (const cutout of cutouts) {
    const hasContainer = isCutoutInsideStoreyWithMargin({
      cutout,
      storey,
      minMarginMeters: 0,
    });
    if (!hasContainer) {
      return new StoreyValidationError(
        StoreyValidationErrors.CUTOUT_PARENT_MISSING,
        {
          storey,
        },
      );
    }

    // Validate the cutout margin is 1m
    const hasMargin = isCutoutInsideStoreyWithMargin({
      cutout,
      storey,
      minMarginMeters: CUTOUT_MIN_MARGIN_METERS,
    });
    if (!hasMargin) {
      return new StoreyValidationError(
        StoreyValidationErrors.CUTOUT_MARGIN_TOO_SMALL,
        {
          storey,
        },
      );
    }
  }

  return true;
};

export const isValidStoreys = (design: BuildingDesign): ValidationResult => {
  const { activities, buildings } = design;
  if (!guards.is.nonEmptyArray(buildings)) {
    return new GenericValidationError(StoreyValidationErrors.BUILDINGS_EMPTY);
  }

  const buildingIdDuplicates = findDuplicates(
    buildings.map((building) => building.id),
  );
  if (buildingIdDuplicates.length) {
    return new GenericValidationError(
      StoreyValidationErrors.DUPLICATE_BUILDING_IDS,
    );
  }

  const allStoreys = buildings.flatMap((building) => building.storeys);
  const storeyIdDuplicates = findDuplicates(allStoreys.map((s) => s.id));
  if (storeyIdDuplicates.length) {
    return new GenericValidationError(
      StoreyValidationErrors.DUPLICATE_STOREY_IDS,
    );
  }
  const shapeIdDuplicates = findDuplicates(
    allStoreys.flatMap((s) => s.shapes.map((shape) => shape.id)),
  );
  if (shapeIdDuplicates.length) {
    return new GenericValidationError(
      StoreyValidationErrors.DUPLICATE_SHAPE_IDS,
    );
  }

  for (const building of buildings) {
    const result = isValidStoreyList(building.storeys, activities);
    if (result !== true) return result;
  }

  const overlappingBuildings = validateBuildingsDoNotOverlap(buildings);
  if (overlappingBuildings !== true) return overlappingBuildings;

  return true;
};

/**
 * Reject designs whose buildings occupy the same 3D volume or hover with a
 * vertical gap over a shared plan. Party walls and slab-to-slab stacking are
 * allowed. Covering buildings that overlap each other on a shared roof are
 * not; see {@link doBuildingSolidsIntersect}.
 */
const validateBuildingsDoNotOverlap = (
  buildings: Building[],
): ValidationResult => {
  for (let i = 0; i < buildings.length; i++) {
    const first = buildings[i];
    if (!first) continue;
    for (let j = i + 1; j < buildings.length; j++) {
      const second = buildings[j];
      if (!second) continue;
      if (doBuildingSolidsIntersect(first, second)) {
        return new GenericValidationError(
          StoreyValidationErrors.BUILDINGS_OVERLAP,
        );
      }
    }
  }
  return true;
};

/**
 * Validate one building's storey list (geometry, mezzanine, connections).
 * @param storeys Storeys of a single building
 * @param activities Design activities referenced by storeys
 */
const isValidStoreyList = (
  storeys: Storey[],
  activities: Activity[],
): ValidationResult => {
  if (!guards.is.nonEmptyArray(storeys)) {
    return new GenericValidationError(StoreyValidationErrors.STOREYS_NOT_ARRAY);
  }

  for (const storey of storeys) {
    const validStorey = isValidStorey(storey, activities);
    if (validStorey !== true) return validStorey;
  }

  // One solid footprint on the ground floor; courtyard houses inside cutouts
  // are extra solids that do not count toward this limit (DEV-3843).
  const bottomStorey = storeys[0];
  if (bottomStorey) {
    const solidsOutsideCutouts = getSolidShapes(bottomStorey).filter(
      (solid) =>
        !isPolygon2D(solid.points) ||
        !isPolygonInsideAnyStoreyCutout(solid.points, bottomStorey),
    );
    if (solidsOutsideCutouts.length > 1) {
      return new GenericValidationError(
        StoreyValidationErrors.BOTTOM_STOREY_MULTIPLE_SHAPES,
      );
    }
  }

  const mezzanineValidation = validateMezzanineStoreys(storeys);
  if (mezzanineValidation !== true) return mezzanineValidation;

  const validShapeConnections = validateShapeConnections(storeys);
  if (validShapeConnections !== true) return validShapeConnections;

  return true;
};

/**
 * Validate mezzanine storeys, including geometry containment against parent storey shapes.
 * Ensures mezzanine metadata and geometry follow Boverket-worthy constraints.
 */
const validateMezzanineStoreys = (storeys: Storey[]): ValidationResult => {
  for (let i = 0; i < storeys.length; i++) {
    const storey = storeys[i];
    if (!designGuards.is.mezzanineStorey(storey)) continue;

    if (i === 0) {
      return new StoreyValidationError(
        StoreyValidationErrors.MEZZANINE_BOTTOM_FORBIDDEN,
        { storey },
      );
    }

    const parentStorey = getStoreyBelow(storeys, storey);

    if (!parentStorey) {
      return new StoreyValidationError(
        StoreyValidationErrors.MEZZANINE_PARENT_MISSING,
        { storey },
      );
    }

    if (
      !isMezzanineAllowed({
        storey,
        storeyBelow: parentStorey,
        isGroundFloor: i === 0,
        parentStoreyHeight: parentStorey.height,
        nextMezzanine: true,
      })
    ) {
      return new StoreyValidationError(
        StoreyValidationErrors.MEZZANINE_NOT_ALLOWED,
        { storey },
      );
    }

    const elevation = storey.mezzanine_elevation;
    if (!guards.is.finiteNumber(elevation) || elevation <= 0) {
      return new StoreyValidationError(
        StoreyValidationErrors.MEZZANINE_ELEVATION_RANGE,
        { storey },
      );
    }
    if (elevation >= parentStorey.height) {
      return new StoreyValidationError(
        StoreyValidationErrors.MEZZANINE_ELEVATION_RANGE,
        { storey },
      );
    }

    const derivedHeight = parentStorey.height - elevation;
    if (
      !guards.is.finiteNumber(storey.height) ||
      !isCloseTo(storey.height, derivedHeight, 0.001)
    ) {
      return new StoreyValidationError(
        StoreyValidationErrors.MEZZANINE_HEIGHT_MISMATCH,
        { storey },
      );
    }

    for (const shape of storey.shapes) {
      const isInsideParent = isShapeInsideParentStoreyShapes(
        shape,
        parentStorey,
      );
      if (!isInsideParent) {
        return new StoreyValidationError(
          StoreyValidationErrors.MEZZANINE_POINTS_OUTSIDE,
          { storey },
        );
      }
    }
  }

  return true;
};

/**
 * Ensure a mezzanine shape is fully contained within its parent storey's shapes.
 * @param mezzanineShape Shape belonging to the mezzanine storey
 * @param parentStorey Parent storey containing the allowed footprint
 */
const isShapeInsideParentStoreyShapes = (
  mezzanineShape: Storey['shapes'][number],
  parentStorey: Storey,
): boolean => {
  if (mezzanineShape.points.length < 3) return false;

  // Quick point inclusion guard to catch obvious violations.
  const allVerticesInside = mezzanineShape.points.every((point) =>
    isPointInsideStoreyShapes(point, parentStorey),
  );
  if (!allVerticesInside) {
    return false;
  }

  // We want edge integrity: sample along each edge to avoid concave leak-through.
  const sampleFractions: readonly number[] = [0.25, 0.5, 0.75];
  for (let i = 0; i < mezzanineShape.points.length; i += 1) {
    const start = mezzanineShape.points[i];
    const end = mezzanineShape.points[(i + 1) % mezzanineShape.points.length];

    if (!start || !end) return false;

    for (const t of sampleFractions) {
      const samplePoint: Point2D = getPointOnLine([start, end], t);

      if (!isPointInsideStoreyShapes(samplePoint, parentStorey)) return false;
    }
  }

  return true;
};

/**
 * Check that solid shapes are vertically stacked: each solid on an upper storey must
 * intersect at least one shape on the storey below. Cutout shapes are excluded.
 * @param storeys Array of storeys to validate
 * @returns ValidationResult
 */
export const validateShapeConnections = (
  storeys: Storey[],
): ValidationResult => {
  // No need to validate if there's only one or zero storeys
  if (storeys.length <= 1) return true;

  // Check each storey starting from the second one (index 1)
  for (let i = 1; i < storeys.length; i++) {
    const currentStorey = storeys[i];
    const previousStorey = currentStorey
      ? getStoreyBelow(storeys, currentStorey)
      : undefined;
    if (!currentStorey || !previousStorey) continue;

    const isConnected = isStoreyConnectedToBelow(currentStorey, previousStorey);
    if (!isConnected) {
      return new StoreyValidationError(
        StoreyValidationErrors.SHAPES_NOT_CONNECTED,
        { storey: currentStorey },
      );
    }
  }

  return true;
};

/**
 * Check whether each solid (non-cutout) shape on {@link storey} intersects at least one
 * shape on {@link storeyBelow}. Cutouts are omitted: they are already required to sit inside
 * a solid on the same storey and must not force overlap with the storey below.
 * @param storey Storey to validate for connection
 * @param storeyBelow Storey directly below for intersection checks
 * @returns true when every solid shape intersects some shape below
 */
const isStoreyConnectedToBelow = (
  storey: Storey,
  storeyBelow: Storey,
): boolean => {
  const solidShapes = getSolidShapes(storey);
  if (
    !storey.shapes.length ||
    !solidShapes.length ||
    !storeyBelow.shapes.length
  ) {
    return false;
  }
  return solidShapes.every((shape) =>
    storeyBelow.shapes.some((belowShape) =>
      doShapesIntersect(shape, belowShape),
    ),
  );
};

/**
 * Validate a storey
 * @param storey Storey to validate
 * @param activities If activities are provided, validate the activity references else ignore them
 * @returns Validated item if valid, otherwise the error
 */
export const validateStorey = (
  storey: Storey,
  activities?: Activity[],
): ValidatedValue<Storey> => {
  const valid = isValidStorey(storey, activities);
  return guards.is.error(valid) ? valid : storey;
};

const getPointDuplicates = (points: Point2D[]): Point2D[] => {
  return points.filter((point, index) =>
    points.some((p, i) => i !== index && isEqualPoints(p, point)),
  );
};
